import { LearningObjectTypeIcon } from '@hendrx/core/common/components/content/LearningObjectTypeIcon';
import {
  BinPage,
  getTypeOptions,
  useFetchLearningObjects,
  useFetchFilterOptions,
  useUrlFilters
} from '@hendrx/core/dashboard';
import { PBL_EDIT_PATH } from '@hendrx/core/pbl/consts/paths';
import { LearningObjectTypes } from '@hendrx/lms/constants';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LEARNING_ACTIVITY_EDIT_PATH, PLENARY_EDIT_PATH, STUDIO_COURSE_EDIT_PATH } from 'shared/common/consts/paths';
import { DEFAULT_LEARNING_OBJECTS_FILTERS } from 'shared/common/dtos/learningObject';

const getLearningItemHref = (id: string, type?: LearningObjectTypes) => {
  switch (type) {
    case LearningObjectTypes.Course:
      return `${STUDIO_COURSE_EDIT_PATH}/${id}`;
    case LearningObjectTypes.LearningActivity:
      return `${LEARNING_ACTIVITY_EDIT_PATH}/${id}`;
    case LearningObjectTypes.PlenaryLesson:
      return `${PLENARY_EDIT_PATH}/${id}`;
    case LearningObjectTypes.PBL:
      return `${PBL_EDIT_PATH}/${id}`;
    default:
      return '';
  }
};

export const Route = createFileRoute('/studio/bin')({
  component: BinPageComponent
});

function BinPageComponent() {
  const { t } = useTranslation('components');

  // Use URL-synced filters
  const { filters, page, perPage, handleFiltersChange } = useUrlFilters();

  // Fetch deleted items using the new API
  const { data, isLoading, isFetching } = useFetchLearningObjects({
    isDeleted: true,
    isPublish: filters.isPublish,
    order: 'DESC',
    page,
    perPage,
    search: filters.search,
    sortBy: 'updatedAt',
    tags: filters.tags,
    types: filters.types
  });

  // Fetch filter options (tags with counts) for deleted items
  const { data: filterOptions } = useFetchFilterOptions({
    types: filters.types.length > 0 ? filters.types : DEFAULT_LEARNING_OBJECTS_FILTERS.types,
    isDeleted: true,
    isPublish: filters.isPublish
  });

  const learningItems = useMemo(() => {
    const itemsArray = data?.data;
    if (!itemsArray || !Array.isArray(itemsArray)) {
      return [];
    }

    return itemsArray.map(item => {
      const itemType = item.type as LearningObjectTypes;
      return {
        _id: item.id,
        title: item.title || '',
        updatedAt: new Date(item.updatedAt),
        isPublished: item.isPublished ?? false,
        type: itemType,
        href: getLearningItemHref(item.id, itemType),
        icon: <LearningObjectTypeIcon type={itemType} />,
        metadata: {
          tags: item.metadata.tags || [],
          thumbnailUrl: item.metadata.thumbnailUrl
        }
      };
    });
  }, [data]);

  const pagination = useMemo(() => {
    if (!data?.pagination) {
      return undefined;
    }
    return data.pagination;
  }, [data?.pagination]);

  const typeOptions = useMemo(() => getTypeOptions(key => t(`activitiesCards.${key}`)), [t]);

  const filtersProp = useMemo(
    () => ({
      types: filters.types,
      isPublish: filters.isPublish,
      tags: filters.tags,
      search: filters.search
    }),
    [filters.types, filters.isPublish, filters.tags, filters.search]
  );

  return (
    <BinPage
      learningItems={learningItems}
      isLoading={isLoading}
      showOverlay={isFetching && !isLoading}
      tagsList={filterOptions?.tags}
      deletedMode
      typeOptions={typeOptions}
      filters={filtersProp}
      onFiltersChange={handleFiltersChange}
      pagination={pagination}
    />
  );
}
